import React, { useState, useEffect, useMemo, Fragment } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowDownToLine,
  ArrowLeft,
  ArrowUpFromLine,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Edit,
  Loader2,
  Play,
  Plus,
  RefreshCw,
  Repeat,
  Search,
  Trash2,
  User,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";

import { API, formatEST, dataCache } from "./utils";


function CheckInsOutsTab({ initialCreateMode = false, onUserClick }) {
  const [pickupReturns, setPickupReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createMode, setCreateMode] = useState(initialCreateMode);
  const [poles, setPoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  
  // Selected poles with their actions
  const [selectedPoles, setSelectedPoles] = useState([]); // [{pole, action, swapPole}]
  
  // User selection (last step, with autocomplete)
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  
  // NEW UI: "Add Pole" flow - shows search when adding
  const [isAddingPole, setIsAddingPole] = useState(false);
  const [currentPole, setCurrentPole] = useState(null); // pole being configured
  const [currentAction, setCurrentAction] = useState('out');
  const [currentSwapPole, setCurrentSwapPole] = useState(null);
  
  // Searchable dropdowns for length/weight
  const [lengthSearch, setLengthSearch] = useState('');
  const [weightSearch, setWeightSearch] = useState('');
  const [showLengthDropdown, setShowLengthDropdown] = useState(false);
  const [showWeightDropdown, setShowWeightDropdown] = useState(false);
  
  // Combined search field (serial, tag, renter name)
  const [searchQuery, setSearchQuery] = useState('');
  const [foundPoles, setFoundPoles] = useState([]);
  
  // For swap pole selection
  const [swapLengthSearch, setSwapLengthSearch] = useState('');
  const [swapWeightSearch, setSwapWeightSearch] = useState('');
  const [swapSerialSearch, setSwapSerialSearch] = useState('');
  const [showSwapLengthDropdown, setShowSwapLengthDropdown] = useState(false);
  const [showSwapWeightDropdown, setShowSwapWeightDropdown] = useState(false);
  const [foundSwapPoles, setFoundSwapPoles] = useState([]);
  
  // Create new pole modal
  const [showCreatePoleModal, setShowCreatePoleModal] = useState(false);
  const [newPoleData, setNewPoleData] = useState({ length: '', weight: '', brand: 'ESSX', serial_num: '', tag_id: '' });
  const [creatingPole, setCreatingPole] = useState(false);
  
  // Notes for the batch
  const [notes, setNotes] = useState('');
  
  // Track expanded batch groups (for list view)
  const [expandedBatches, setExpandedBatches] = useState(new Set());
  
  // Multi-select for changing processing status in history view
  const [selectedHistoryIds, setSelectedHistoryIds] = useState(new Set());
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  // Search and pagination for history list
  const [historySearch, setHistorySearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 50;

  // Process action dialog state
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [processingItem, setProcessingItem] = useState(null);
  const [eligibleRentals, setEligibleRentals] = useState([]);
  const [selectedRentalId, setSelectedRentalId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingRentals, setLoadingRentals] = useState(false);
  const [chargeSwapFee, setChargeSwapFee] = useState(false);
  const [chargeProrata, setChargeProrata] = useState(true);
  const [prorataInfo, setProrataInfo] = useState(null); // { daysRemaining, monthlyPrice, dailyRate, amount }

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({ notes: '', status: 0, user_id: null });
  const [editUserSearch, setEditUserSearch] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // Get unique lengths and weights for dropdowns
  const uniqueLengths = useMemo(() => {
    const lengths = [...new Set(poles.map(p => p.length).filter(Boolean))];
    return lengths.sort((a, b) => {
      const aNum = parseFloat(a.replace("'", "."));
      const bNum = parseFloat(b.replace("'", "."));
      return aNum - bNum;
    });
  }, [poles]);

  const uniqueWeights = useMemo(() => {
    const weights = [...new Set(poles.map(p => p.weight).filter(Boolean))];
    return weights.sort((a, b) => parseInt(a) - parseInt(b));
  }, [poles]);
  
  // Filtered lengths based on search
  const filteredLengths = useMemo(() => {
    if (!lengthSearch) return uniqueLengths;
    return uniqueLengths.filter(l => l.toLowerCase().includes(lengthSearch.toLowerCase()));
  }, [uniqueLengths, lengthSearch]);
  
  // Filtered weights based on search
  const filteredWeights = useMemo(() => {
    if (!weightSearch) return uniqueWeights;
    return uniqueWeights.filter(w => w.toString().includes(weightSearch));
  }, [uniqueWeights, weightSearch]);
  
  // Filtered swap lengths/weights
  const filteredSwapLengths = useMemo(() => {
    if (!swapLengthSearch) return uniqueLengths;
    return uniqueLengths.filter(l => l.toLowerCase().includes(swapLengthSearch.toLowerCase()));
  }, [uniqueLengths, swapLengthSearch]);
  
  const filteredSwapWeights = useMemo(() => {
    if (!swapWeightSearch) return uniqueWeights;
    return uniqueWeights.filter(w => w.toString().includes(swapWeightSearch));
  }, [uniqueWeights, swapWeightSearch]);
  
  // Auto-suggest user based on selected poles' renters
  const suggestedUser = useMemo(() => {
    const renters = selectedPoles
      .filter(sp => sp.pole.current_renter || sp.pole.renter_name)
      .map(sp => sp.pole.renter_name || sp.pole.current_renter);
    if (renters.length > 0) {
      const renterCounts = {};
      renters.forEach(r => { renterCounts[r] = (renterCounts[r] || 0) + 1; });
      const topRenter = Object.entries(renterCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
      return users.find(u => u.full_name === topRenter);
    }
    return null;
  }, [selectedPoles, users]);

  // Group pickup returns by batch_id for list view
  // Filter pickup returns by search
  const filteredPickupReturns = useMemo(() => {
    if (!historySearch.trim()) return pickupReturns;
    const q = historySearch.trim().toLowerCase();
    return pickupReturns.filter(item => {
      const fields = [
        item.user_name,
        item.user_email,
        item.pole_length,
        item.pole_weight,
        item.pole_code,
        item.pole_serial,
        item.notes,
        item.swap_pole_length,
        item.swap_pole_code,
        String(item.id),
        String(item.rental_id || ''),
        item.processing_status,
      ];
      return fields.some(f => (f || '').toLowerCase().includes(q));
    });
  }, [pickupReturns, historySearch]);

  const groupedPickupReturns = useMemo(() => {
    const groups = {};
    const ungrouped = [];
    
    filteredPickupReturns.forEach(item => {
      if (item.batch_id && item.batch_id.trim()) {
        if (!groups[item.batch_id]) {
          groups[item.batch_id] = {
            batch_id: item.batch_id,
            items: [],
            created_at: item.created_at,
            user_name: item.user_name,
            user_email: item.user_email
          };
        }
        groups[item.batch_id].items.push(item);
      } else {
        ungrouped.push(item);
      }
    });
    
    const groupedArray = Object.values(groups).map(g => ({
      ...g,
      isGroup: true
    }));
    
    const ungroupedArray = ungrouped.map(item => ({
      ...item,
      isGroup: false
    }));
    
    return [...groupedArray, ...ungroupedArray].sort((a, b) => {
      const dateA = a.created_at || a.items?.[0]?.created_at;
      const dateB = b.created_at || b.items?.[0]?.created_at;
      return new Date(dateB) - new Date(dateA);
    });
  }, [filteredPickupReturns]);

  // Paginated items
  const totalPages = Math.ceil(groupedPickupReturns.length / ITEMS_PER_PAGE);
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return groupedPickupReturns.slice(start, start + ITEMS_PER_PAGE);
  }, [groupedPickupReturns, currentPage]);

  // Reset page when search changes
  useEffect(() => { setCurrentPage(1); }, [historySearch]);

  const fetchPickupReturns = async (forceRefresh = false) => {
    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = dataCache.get('pickup_returns');
      if (cached) {
        setPickupReturns(cached);
        setLoading(false);
        return;
      }
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/pickup-returns`);
      const data = response.data.items || [];
      setPickupReturns(data);
      dataCache.set('pickup_returns', data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      toast.error("Failed to load check-ins/outs");
    } finally {
      setLoading(false);
    }
  };

  const fetchPolesAndUsers = async (forceRefresh = false) => {
    // Check cache first
    if (!forceRefresh) {
      const cachedPoles = dataCache.get('all_poles');
      const cachedUsers = dataCache.get('all_users');
      if (cachedPoles && cachedUsers) {
        setPoles(cachedPoles);
        setUsers(cachedUsers);
        return;
      }
    }
    
    try {
      const [polesRes, usersRes] = await Promise.all([
        axios.get(`${API}/poles/all`),
        axios.get(`${API}/users/all`)
      ]);
      const polesData = polesRes.data.poles || [];
      const usersData = usersRes.data.users || [];
      setPoles(polesData);
      setUsers(usersData);
      dataCache.set('all_poles', polesData);
      dataCache.set('all_users', usersData);
    } catch (err) {
      console.error("Failed to fetch poles/users:", err);
    }
  };

  useEffect(() => { 
    fetchPickupReturns(); 
    fetchPolesAndUsers();
  }, []);

  // Search for poles based on length/weight OR search query (serial, tag, renter name)
  useEffect(() => {
    if (!isAddingPole) {
      setFoundPoles([]);
      return;
    }
    
    let results = poles;
    
    // If search query is provided (serial, tag, pole code, or RENTER NAME)
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      results = results.filter(pole => 
        pole.serial_num?.toLowerCase().includes(q) ||
        pole.tag_id?.toString().includes(q) ||
        pole.pole_code?.toLowerCase().includes(q) ||
        pole.current_renter?.toLowerCase().includes(q) ||
        pole.renter_name?.toLowerCase().includes(q) // Search by renter name
      );
    } else {
      // Filter by length (from searchable dropdown)
      if (lengthSearch && uniqueLengths.includes(lengthSearch)) {
        results = results.filter(p => p.length === lengthSearch);
      }
      // Filter by weight (from searchable dropdown)
      if (weightSearch && uniqueWeights.map(String).includes(weightSearch)) {
        results = results.filter(p => p.weight?.toString() === weightSearch);
      }
    }
    
    // Exclude already selected poles
    const selectedIds = selectedPoles.map(sp => sp.pole.id);
    results = results.filter(p => !selectedIds.includes(p.id));
    
    // Show results if any filter is applied
    const hasLengthFilter = lengthSearch && uniqueLengths.includes(lengthSearch);
    const hasWeightFilter = weightSearch && uniqueWeights.map(String).includes(weightSearch);
    if (hasLengthFilter || hasWeightFilter || searchQuery.trim()) {
      setFoundPoles(results.slice(0, 30));
    } else {
      setFoundPoles([]);
    }
  }, [lengthSearch, weightSearch, searchQuery, poles, isAddingPole, selectedPoles, uniqueLengths, uniqueWeights]);

  // Search swap poles (only available ones) with serial number display
  useEffect(() => {
    if (currentAction !== 'swap') {
      setFoundSwapPoles([]);
      return;
    }
    
    let availablePoles = poles.filter(p => 
      !p.current_renter && !p.renter_name && (p.status === 'Available' || p.status === 'available' || !p.status)
    );
    
    // Filter by length
    if (swapLengthSearch && uniqueLengths.includes(swapLengthSearch)) {
      availablePoles = availablePoles.filter(p => p.length === swapLengthSearch);
    }
    // Filter by weight
    if (swapWeightSearch && uniqueWeights.map(String).includes(swapWeightSearch)) {
      availablePoles = availablePoles.filter(p => p.weight?.toString() === swapWeightSearch);
    }
    // Filter by serial number or tag
    if (swapSerialSearch) {
      availablePoles = availablePoles.filter(p => 
        p.serial_num?.toLowerCase().includes(swapSerialSearch.toLowerCase()) ||
        p.tag_id?.toString().includes(swapSerialSearch)
      );
    }
    
    // Exclude currently selected pole
    if (currentPole) {
      availablePoles = availablePoles.filter(p => p.id !== currentPole.id);
    }
    
    const hasLengthFilter = swapLengthSearch && uniqueLengths.includes(swapLengthSearch);
    const hasWeightFilter = swapWeightSearch && uniqueWeights.map(String).includes(swapWeightSearch);
    const hasSerialFilter = swapSerialSearch && swapSerialSearch.length >= 2;
    if (hasLengthFilter || hasWeightFilter || hasSerialFilter) {
      setFoundSwapPoles(availablePoles.slice(0, 20));
    } else {
      setFoundSwapPoles([]);
    }
  }, [swapLengthSearch, swapWeightSearch, swapSerialSearch, poles, currentAction, currentPole, uniqueLengths, uniqueWeights]);

  // Filter users by search term
  const filteredUsers = users.filter(u => 
    userSearch === '' || 
    u.full_name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase())
  ).slice(0, 15);

  // Start adding a new pole
  const startAddingPole = () => {
    setIsAddingPole(true);
    setCurrentPole(null);
    setCurrentAction('out');
    setCurrentSwapPole(null);
    setLengthSearch('');
    setWeightSearch('');
    setSearchQuery('');
    setSwapLengthSearch('');
    setSwapWeightSearch('');
    setFoundPoles([]);
    setFoundSwapPoles([]);
  };
  
  // Select a pole during "Add Pole" flow
  const selectPoleForAdding = (pole) => {
    setCurrentPole(pole);
    // If pole is rented, default to Return action
    const isRented = pole.current_renter || pole.renter_name;
    setCurrentAction(isRented ? 'in' : 'out');
    setCurrentSwapPole(null);
    // Clear search but keep dropdowns
    setSearchQuery('');
    setFoundPoles([]);
    
    // If pole is rented, auto-select the renter in the user dropdown
    if (isRented && pole.user_id) {
      const renter = users.find(u => u.id === pole.user_id);
      if (renter) {
        setSelectedUser(renter);
      }
    }
  };
  
  // Confirm adding the current pole to selection
  const confirmAddPole = () => {
    if (!currentPole) {
      toast.error("Please select a pole");
      return;
    }
    if (currentAction === 'swap' && !currentSwapPole) {
      toast.error("Please select a swap pole from inventory");
      return;
    }
    
    setSelectedPoles([...selectedPoles, {
      pole: currentPole,
      action: currentAction,
      swapPole: currentAction === 'swap' ? currentSwapPole : null
    }]);
    
    // Reset for next pole but stay in adding mode
    setCurrentPole(null);
    setCurrentAction('out');
    setCurrentSwapPole(null);
    setLengthSearch('');
    setWeightSearch('');
    setSearchQuery('');
    setSwapLengthSearch('');
    setSwapWeightSearch('');
    setFoundPoles([]);
    setFoundSwapPoles([]);
    
    toast.success(`Added ${currentPole.length} - ${currentPole.weight}`);
  };
  
  // Cancel adding (but keep existing selections)
  const cancelAdding = () => {
    setIsAddingPole(false);
    setCurrentPole(null);
    setCurrentAction('out');
    setCurrentSwapPole(null);
    setEditingIndex(null);
  };
  
  // State for editing an existing selected pole
  const [editingIndex, setEditingIndex] = useState(null);
  
  // Start editing an existing selected pole
  const startEditingPole = (index) => {
    const item = selectedPoles[index];
    setEditingIndex(index);
    setCurrentPole(item.pole);
    setCurrentAction(item.action);
    setCurrentSwapPole(item.swapPole);
    setIsAddingPole(true);
    setLengthSearch('');
    setWeightSearch('');
    setSearchQuery('');
  };
  
  // Update an existing pole in the selection
  const updateSelectedPole = () => {
    if (editingIndex === null || !currentPole) return;
    
    if (currentAction === 'swap' && !currentSwapPole) {
      toast.error("Please select a swap pole from inventory");
      return;
    }
    
    const updated = [...selectedPoles];
    updated[editingIndex] = {
      pole: currentPole,
      action: currentAction,
      swapPole: currentAction === 'swap' ? currentSwapPole : null
    };
    setSelectedPoles(updated);
    
    // Reset
    setEditingIndex(null);
    setCurrentPole(null);
    setCurrentAction('out');
    setCurrentSwapPole(null);
    setIsAddingPole(false);
    
    toast.success("Updated pole action");
  };
  
  // Remove pole from selection
  const removePoleFromSelection = (index) => {
    setSelectedPoles(selectedPoles.filter((_, i) => i !== index));
  };
  
  // Apply suggested user
  useEffect(() => {
    if (suggestedUser && !selectedUser) {
      setSelectedUser(suggestedUser);
      setUserSearch(suggestedUser.full_name);
    }
  }, [suggestedUser]);

  // Submit all selected items
  // IMPORTANT: In pickup_returns table:
  // status = 0 means CHECK-OUT (pole goes OUT to customer)
  // status = 1 means CHECK-IN (pole comes back IN from customer)
  const submitSelectedItems = async () => {
    if (selectedPoles.length === 0) {
      toast.error("No poles selected");
      return;
    }
    if (!selectedUser) {
      toast.error("Please select a user");
      return;
    }
    
    // Validate all swap items have a swap pole selected
    const missingSwap = selectedPoles.find(sp => sp.action === 'swap' && !sp.swapPole);
    if (missingSwap) {
      toast.error(`Please select a replacement pole for: ${missingSwap.pole.length} - ${missingSwap.pole.weight}`);
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/pickup-returns/batch`, {
        items: selectedPoles.map(item => ({
          pole_id: item.pole.id,
          user_id: selectedUser.id,
          // status=0 for OUT (pole leaving inventory), status=1 for IN (pole returning)
          status: item.action === 'in' ? 1 : 0,
          swap: item.action === 'swap',
          swap_pole_id: item.swapPole?.id || null,
          notes: notes
        }))
      });
      
      toast.success(`Successfully processed ${selectedPoles.length} item(s)`);
      setSelectedPoles([]);
      setSelectedUser(null);
      setUserSearch('');
      setNotes('');
      setCreateMode(false);
      fetchPickupReturns();
      fetchPolesAndUsers(); // Refresh poles to update statuses
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit items");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusLower = (status || '').toLowerCase();
    switch (statusLower) {
      case 'active':
      case 'rented':
        return <Badge className="bg-blue-500 text-white text-xs">Rented</Badge>;
      case 'available':
      case 'in stock':
        return <Badge className="bg-emerald-500 text-white text-xs">Available</Badge>;
      case 'broken':
        return <Badge variant="destructive" className="text-xs">Broken</Badge>;
      case 'awaiting pickup':
        return <Badge className="bg-amber-500 text-white text-xs">Awaiting Pickup</Badge>;
      case 'pole requested':
      case 'requested':
        return <Badge className="bg-purple-500 text-white text-xs">Requested</Badge>;
      default:
        return <Badge variant="outline" className="text-xs">{status || 'Unknown'}</Badge>;
    }
  };

  // Get pole status - check if currently rented
  const getPoleDisplayStatus = (pole) => {
    if (pole.current_renter || pole.renter_name) {
      return 'Rented';
    }
    return pole.status || 'Available';
  };

  // Action badge shows what happened in history
  // status=0 = Checkout (pole went to customer), status=1 = Return (pole came back)
  const getActionBadge = (status, swap) => {
    if (swap) return <Badge variant="outline" className="bg-purple-50 text-purple-600 border-purple-300"><Repeat className="w-3 h-3 mr-1" />Swap</Badge>;
    if (status === 0) return <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-300"><ArrowUpFromLine className="w-3 h-3 mr-1" />Checkout</Badge>;
    return <Badge variant="outline" className="bg-green-50 text-green-600 border-green-300"><ArrowDownToLine className="w-3 h-3 mr-1" />Return</Badge>;
  };

  // Create new pole handler
  const handleCreatePole = async () => {
    if (!newPoleData.length || !newPoleData.weight) {
      toast.error("Length and weight are required");
      return;
    }
    
    setCreatingPole(true);
    try {
      const response = await axios.post(`${API}/poles/create`, newPoleData);
      toast.success(`Pole created: ${newPoleData.length} - ${newPoleData.weight}`);
      setShowCreatePoleModal(false);
      setNewPoleData({ length: '', weight: '', brand: 'ESSX', serial_num: '', tag_id: '' });
      fetchPolesAndUsers(); // Refresh poles list
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create pole");
    } finally {
      setCreatingPole(false);
    }
  };

  // Toggle batch expand/collapse for list view
  const toggleBatch = (batchId) => {
    setExpandedBatches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(batchId)) {
        newSet.delete(batchId);
      } else {
        newSet.add(batchId);
      }
      return newSet;
    });
  };
  
  // Batches default to collapsed - no auto-expand

  // Get all individual item IDs from the pickup returns list
  const allHistoryItemIds = useMemo(() => {
    const ids = [];
    pickupReturns.forEach(item => {
      ids.push(item.id);
    });
    return ids;
  }, [pickupReturns]);

  // Toggle selection for a single history item
  const toggleHistorySelection = (id) => {
    setSelectedHistoryIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Toggle all selections
  const toggleSelectAllHistory = () => {
    if (selectedHistoryIds.size === allHistoryItemIds.length) {
      setSelectedHistoryIds(new Set());
    } else {
      setSelectedHistoryIds(new Set(allHistoryItemIds));
    }
  };

  // Update processing status for selected items
  const updateProcessingStatus = async (newStatus) => {
    if (selectedHistoryIds.size === 0) {
      toast.error("No items selected");
      return;
    }
    
    setUpdatingStatus(true);
    try {
      const response = await axios.put(`${API}/pickup-returns/status`, {
        ids: Array.from(selectedHistoryIds),
        processing_status: newStatus
      });
      
      toast.success(`Updated ${response.data.updated_count} item(s) to "${newStatus}"`);
      setSelectedHistoryIds(new Set());
      fetchPickupReturns(); // Refresh the list
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Open process action dialog
  const openProcessDialog = async (item) => {
    setProcessingItem(item);
    setProcessDialogOpen(true);
    setSelectedRentalId(null);
    setEligibleRentals([]);
    setChargeSwapFee(false); // Reset swap fee checkbox
    setChargeProrata(true); // Default to charge prorata
    setProrataInfo(null);
    
    // For checkout (status 0), fetch eligible rentals
    if (item.status === 0 && !item.swap) {
      setLoadingRentals(true);
      try {
        const response = await axios.get(`${API}/pickup-returns/${item.id}/eligible-rentals`);
        const rentals = response.data.rentals || [];
        setEligibleRentals(rentals);
        // Auto-select the first rental if available
        if (rentals.length > 0) {
          setSelectedRentalId(rentals[0].id);
          // Fetch proration info if the rental has active subscription
          if (rentals[0].subscription_status === 'active') {
            fetchProrataInfo(item, rentals[0].id);
          }
        }
      } catch (err) {
        console.error('Error fetching eligible rentals:', err);
      } finally {
        setLoadingRentals(false);
      }
    }
  };
  
  // Fetch proration info for a rental
  const fetchProrataInfo = async (item, rentalId) => {
    try {
      const response = await axios.get(`${API}/pickup-returns/calculate-prorata?pickup_return_id=${item.id}&rental_id=${rentalId}`);
      if (response.data) {
        setProrataInfo(response.data);
      }
    } catch (err) {
      console.error('Error fetching prorata info:', err);
      setProrataInfo(null);
    }
  };
  
  // Handle rental selection change
  const handleRentalSelect = (rentalId) => {
    setSelectedRentalId(rentalId);
    setProrataInfo(null);
    
    // Find the selected rental and fetch prorata if it has active subscription
    const rental = eligibleRentals.find(r => r.id === rentalId);
    if (rental?.subscription_status === 'active' && processingItem) {
      fetchProrataInfo(processingItem, rentalId);
    }
  };

  // Process the action (checkout/return/swap)
  const handleProcessAction = async () => {
    if (!processingItem) return;
    
    setIsProcessing(true);
    try {
      let response;
      const item = processingItem;
      
      if (item.swap) {
        // Process swap
        response = await axios.post(`${API}/pickup-returns/process-swap`, {
          pickup_return_id: item.id,
          charge_swap_fee: chargeSwapFee
        });
        toast.success(response.data.message || "Swap processed successfully");
        if (response.data.swap_fee_charged) {
          toast.info("Swap fee charged successfully");
        } else if (response.data.swap_fee_added) {
          toast.info("Swap fee will be charged on next invoice");
        }
      } else if (item.status === 0) {
        // Process checkout - add pole to rental (status 0 = checkout/out)
        if (!selectedRentalId && eligibleRentals.length > 0) {
          toast.error("Please select a rental or create a new one");
          setIsProcessing(false);
          return;
        }
        
        response = await axios.post(`${API}/pickup-returns/process-checkout`, {
          pickup_return_id: item.id,
          rental_id: selectedRentalId === 'new' ? null : selectedRentalId,
          create_new_rental: selectedRentalId === 'new' || (!selectedRentalId && eligibleRentals.length === 0),
          charge_prorata: chargeProrata
        });
        toast.success(response.data.message || "Checkout processed successfully");
        if (response.data.created_new_rental) {
          toast.info(`Created new rental #${response.data.rental_id}`);
        }
        if (response.data.subscription_updated) {
          toast.info("Stripe subscription updated with new pole");
        }
        if (response.data.prorata_charged) {
          toast.info(`Prorated amount of $${response.data.prorata_amount?.toFixed(2)} charged`);
        }
        if (response.data.already_in_rental) {
          // Pole was already in a rental - offer to view it
          toast.info(
            <div className="flex items-center gap-2">
              <span>Pole already in rental</span>
              <a 
                href={`/rentals/${response.data.rental_id}`} 
                className="underline font-medium"
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = `/rentals/${response.data.rental_id}`;
                }}
              >
                View Rental #{response.data.rental_id}
              </a>
            </div>,
            { duration: 5000 }
          );
        }
      } else if (item.status === 1) {
        // Process return (status 1 = return/in)
        response = await axios.post(`${API}/pickup-returns/process-return`, {
          pickup_return_id: item.id
        });
        toast.success(response.data.message || "Return processed successfully");
        if (response.data.rental_completed) {
          toast.info("Rental marked as completed (no poles remaining)");
        }
        if (response.data.subscription_updated) {
          toast.info("Subscription updated with new pricing");
        }
        if (response.data.subscription_cancelled) {
          toast.info("Subscription cancelled");
        }
      }
      
      setProcessDialogOpen(false);
      setProcessingItem(null);
      fetchPickupReturns(true); // Refresh the list
      
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to process action");
    } finally {
      setIsProcessing(false);
    }
  };

  // Process All — runs every unprocessed item in a batch group sequentially.
  // Asks ONCE upfront whether to charge prorata (only if any checkouts exist)
  // and whether to charge a swap fee (only if any swaps — labeled OR implicit — exist).
  // Order: implicit swap pairs (checkout first, return second, then fee)
  //   → standalone checkouts → standalone returns → labeled swaps.
  const [processAllRunning, setProcessAllRunning] = useState(false);
  const processAllInBatch = async (group) => {
    if (processAllRunning) return;
    const unprocessed = (group.items || []).filter(i => i.processing_status !== 'completed');
    if (unprocessed.length === 0) {
      toast.info("Nothing to process in this batch.");
      return;
    }

    const labeledSwaps = unprocessed.filter(i => i.swap);
    const checkouts = unprocessed.filter(i => !i.swap && i.status === 0);
    const returns = unprocessed.filter(i => !i.swap && i.status === 1);

    // Implicit swap pairs = min(checkouts, returns); remaining are standalone.
    const pairCount = Math.min(checkouts.length, returns.length);
    const pairCheckouts = checkouts.slice(0, pairCount);
    const pairReturns = returns.slice(0, pairCount);
    const standaloneCheckouts = checkouts.slice(pairCount);
    const standaloneReturns = returns.slice(pairCount);
    const totalSwaps = labeledSwaps.length + pairCount;

    // Prompt 1: prorata (only if any checkouts will run)
    const willRunCheckouts = standaloneCheckouts.length + pairCheckouts.length > 0;
    let chargeProrataAll = true;
    if (willRunCheckouts) {
      const want = window.confirm(
        `${standaloneCheckouts.length + pairCheckouts.length} checkout(s) will run.\n\n` +
        `Click OK to CHARGE prorated billing on all of them.\n` +
        `Click Cancel to SKIP proration on all of them.`
      );
      chargeProrataAll = want;
    }

    // Prompt 2: swap fee (only if any swaps)
    let chargeSwapFeeAll = false;
    if (totalSwaps > 0) {
      const want = window.confirm(
        `${totalSwaps} swap(s) detected (${labeledSwaps.length} labeled + ${pairCount} implicit checkout/return pair${pairCount === 1 ? '' : 's'}).\n\n` +
        `Click OK to charge the $35 SWAP FEE on each.\n` +
        `Click Cancel to skip the swap fee on all of them.`
      );
      chargeSwapFeeAll = want;
    }

    // Final confirmation summary
    const summary = [
      pairCount > 0 ? `${pairCount} implicit swap pair${pairCount === 1 ? '' : 's'}` : null,
      labeledSwaps.length > 0 ? `${labeledSwaps.length} labeled swap${labeledSwaps.length === 1 ? '' : 's'}` : null,
      standaloneCheckouts.length > 0 ? `${standaloneCheckouts.length} checkout${standaloneCheckouts.length === 1 ? '' : 's'}` : null,
      standaloneReturns.length > 0 ? `${standaloneReturns.length} return${standaloneReturns.length === 1 ? '' : 's'}` : null,
    ].filter(Boolean).join(', ');
    if (!window.confirm(`Process All — ${summary}?\n\nProration: ${chargeProrataAll ? 'CHARGE' : 'skip'}\nSwap fee: ${chargeSwapFeeAll ? 'CHARGE' : 'skip'}`)) {
      return;
    }

    setProcessAllRunning(true);
    let okCount = 0;
    const errors = [];

    // For the whole batch, track the "active rental" — once we create or detect one,
    // every subsequent checkout MUST attach to it (don't create N rentals for one customer).
    let activeRentalId = unprocessed.map(i => i.rental_id).find(Boolean) || null;

    // No rental_id on the rows? Look up an existing eligible rental for this user
    // BEFORE creating a new one. Use the first unprocessed checkout's eligibility
    // endpoint to find it.
    if (!activeRentalId) {
      const firstCheckout = [...pairCheckouts, ...standaloneCheckouts][0];
      if (firstCheckout) {
        try {
          const elig = await axios.get(`${API}/pickup-returns/${firstCheckout.id}/eligible-rentals`);
          const list = elig.data?.rentals || [];
          if (list.length > 0) {
            activeRentalId = list[0].id;
          }
        } catch (e) {
          // non-fatal — just fall through to create_new_rental
        }
      }
    }

    const checkoutBody = (item) => {
      const rentalId = activeRentalId || item.rental_id || null;
      return {
        pickup_return_id: item.id,
        rental_id: rentalId,
        create_new_rental: !rentalId,
        charge_prorata: chargeProrataAll,
      };
    };

    try {
      // 1) Implicit swap pairs: checkout side first, return side second, then fee
      for (let i = 0; i < pairCount; i++) {
        const c = pairCheckouts[i];
        const r = pairReturns[i];
        try {
          const coRes = await axios.post(`${API}/pickup-returns/process-checkout`, checkoutBody(c));
          if (coRes.data?.rental_id) activeRentalId = coRes.data.rental_id;
          await axios.post(`${API}/pickup-returns/process-return`, { pickup_return_id: r.id });
          okCount += 2;
          if (chargeSwapFeeAll) {
            const rentalId = activeRentalId || r.rental_id || c.rental_id;
            if (rentalId) {
              try {
                await axios.post(`${API}/pickup-returns/charge-swap-fee`, {
                  rental_id: rentalId,
                  old_pole_label: `${r.pole_length} - ${r.pole_weight}`,
                  new_pole_label: `${c.pole_length} - ${c.pole_weight}`,
                });
              } catch (feeErr) {
                errors.push(`Swap fee (pair ${i + 1}): ${feeErr.response?.data?.detail || feeErr.message}`);
              }
            }
          }
        } catch (err) {
          errors.push(`Swap pair ${i + 1} (#${c.id}/#${r.id}): ${err.response?.data?.detail || err.message}`);
        }
      }

      // 2) Standalone checkouts — all attach to the same rental
      for (const c of standaloneCheckouts) {
        try {
          const coRes = await axios.post(`${API}/pickup-returns/process-checkout`, checkoutBody(c));
          if (coRes.data?.rental_id) activeRentalId = coRes.data.rental_id;
          okCount += 1;
        } catch (err) {
          errors.push(`Checkout #${c.id}: ${err.response?.data?.detail || err.message}`);
        }
      }

      // 3) Standalone returns
      for (const r of standaloneReturns) {
        try {
          await axios.post(`${API}/pickup-returns/process-return`, { pickup_return_id: r.id });
          okCount += 1;
        } catch (err) {
          errors.push(`Return #${r.id}: ${err.response?.data?.detail || err.message}`);
        }
      }

      // 4) Labeled swaps (use the existing swap endpoint, pass the same fee answer)
      for (const s of labeledSwaps) {
        try {
          await axios.post(`${API}/pickup-returns/process-swap`, {
            pickup_return_id: s.id,
            charge_swap_fee: chargeSwapFeeAll,
          });
          okCount += 1;
        } catch (err) {
          errors.push(`Swap #${s.id}: ${err.response?.data?.detail || err.message}`);
        }
      }

      if (errors.length === 0) {
        toast.success(`Processed ${okCount} item(s) successfully.`);
      } else {
        toast.warning(`Processed ${okCount} item(s); ${errors.length} failed.`);
        errors.slice(0, 5).forEach(e => toast.error(e, { duration: 8000 }));
      }
      fetchPickupReturns(true);
    } finally {
      setProcessAllRunning(false);
    }
  };

  // Delete a check-in/checkout record
  const deletePickupReturn = async (recordId, recordType) => {
    if (!window.confirm(`Are you sure you want to delete this ${recordType} record?`)) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/pickup-returns/${recordId}`);
      toast.success(response.data.message || "Record deleted successfully");
      fetchPickupReturns(true); // Refresh the list
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete record");
    }
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setEditForm({
      notes: item.notes || '',
      status: item.status,
      user_id: item.user_id
    });
    setEditUserSearch('');
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;
    setSavingEdit(true);
    try {
      await axios.put(`${API}/pickup-returns/${editingItem.id}`, {
        notes: editForm.notes,
        status: editForm.status,
        user_id: editForm.user_id
      });
      toast.success(`Record #${editingItem.id} updated`);
      setEditDialogOpen(false);
      setEditingItem(null);
      fetchPickupReturns(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update record");
    } finally {
      setSavingEdit(false);
    }
  };

  // Get processing status badge
  const getProcessingStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge variant="default" className="bg-emerald-500"><Check className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'in_progress':
        return <Badge variant="outline" className="text-blue-600 border-blue-300">In Progress</Badge>;
      default:
        return <Badge variant="outline" className="text-amber-600 border-amber-300">Pending</Badge>;
    }
  };

  // Full page create mode - NEW UI: Add Pole button flow
  if (createMode) {
    return (
      <div className="min-h-[calc(100vh-200px)] bg-slate-50 -mx-4 sm:-mx-6 lg:-mx-8 -my-6 px-4 py-6">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => { setCreateMode(false); setSelectedPoles([]); setSelectedUser(null); setIsAddingPole(false); }}>
                <ArrowLeft className="w-4 h-4 mr-1" />Back
              </Button>
              <h2 className="text-xl font-bold text-slate-900">New Checkout / Return</h2>
            </div>
          </div>

          {/* Selected Poles List */}
          {selectedPoles.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">Selected Poles ({selectedPoles.length})</h3>
              </div>
              <div className="space-y-2">
                {selectedPoles.map((item, index) => (
                  <div key={item.pole.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                    <div className="flex-1 cursor-pointer" onClick={() => startEditingPole(index)}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{item.pole.length} - {item.pole.weight}</span>
                        {item.action === 'in' && <Badge className="bg-green-100 text-green-700 text-xs">Return</Badge>}
                        {item.action === 'out' && <Badge className="bg-blue-100 text-blue-700 text-xs">Checkout</Badge>}
                        {item.action === 'swap' && <Badge className="bg-purple-100 text-purple-700 text-xs">Swap</Badge>}
                      </div>
                      <div className="text-xs text-slate-500">
                        SN: {item.pole.serial_num || 'N/A'} | {item.pole.brand}
                        {item.swapPole && <span className="text-purple-600 ml-2">→ {item.swapPole.length} - {item.swapPole.weight}</span>}
                      </div>
                      <div className="text-xs text-blue-500 mt-1">Click to edit</div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removePoleFromSelection(index)} className="text-red-500 h-8 w-8 p-0">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add Pole Section */}
          {isAddingPole ? (
            <div className="bg-white rounded-xl shadow-sm border border-emerald-200 p-4 mb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Plus className="w-4 h-4 text-emerald-600" />
                  {currentPole ? 'Configure Pole Action' : 'Search for Pole'}
                </h3>
                <Button variant="ghost" size="sm" onClick={cancelAdding}>
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {!currentPole ? (
                <>
                  {/* Searchable Length/Weight Dropdowns */}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="relative">
                      <Label className="text-xs mb-1 block">Length</Label>
                      <Input 
                        placeholder="Type to search..." 
                        value={lengthSearch}
                        onChange={(e) => setLengthSearch(e.target.value)}
                        onFocus={() => setShowLengthDropdown(true)}
                        onBlur={() => setTimeout(() => setShowLengthDropdown(false), 200)}
                        className="text-base"
                      />
                      {showLengthDropdown && filteredLengths.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                          {filteredLengths.map(len => (
                            <div 
                              key={len} 
                              className={`p-2 cursor-pointer hover:bg-slate-100 ${lengthSearch === len ? 'bg-emerald-50 text-emerald-700' : ''}`}
                              onMouseDown={() => { setLengthSearch(len); setShowLengthDropdown(false); }}
                            >
                              {len}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="relative">
                      <Label className="text-xs mb-1 block">Weight</Label>
                      <Input 
                        placeholder="Type to search..." 
                        value={weightSearch}
                        onChange={(e) => setWeightSearch(e.target.value)}
                        onFocus={() => setShowWeightDropdown(true)}
                        onBlur={() => setTimeout(() => setShowWeightDropdown(false), 200)}
                        className="text-base"
                      />
                      {showWeightDropdown && filteredWeights.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                          {filteredWeights.map(w => (
                            <div 
                              key={w} 
                              className={`p-2 cursor-pointer hover:bg-slate-100 ${weightSearch === w.toString() ? 'bg-emerald-50 text-emerald-700' : ''}`}
                              onMouseDown={() => { setWeightSearch(w.toString()); setShowWeightDropdown(false); }}
                            >
                              {w}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* OR divider */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex-1 h-px bg-slate-200"></div>
                    <span className="text-xs text-slate-400 font-medium">OR</span>
                    <div className="flex-1 h-px bg-slate-200"></div>
                  </div>

                  {/* Search by Serial/Tag/Renter Name */}
                  <div className="mb-4">
                    <Label className="text-xs mb-1 block">Search by Serial Number, Tag ID, or Renter Name</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input 
                        placeholder="Enter serial, tag, or customer name..." 
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          if (e.target.value) {
                            setLengthSearch('');
                            setWeightSearch('');
                          }
                        }}
                        className="text-base pl-10"
                      />
                    </div>
                  </div>

                  {/* Found Poles */}
                  {foundPoles.length > 0 && (
                    <div className="mb-4">
                      <Label className="text-xs mb-2 block">Click to select ({foundPoles.length} found)</Label>
                      <div className="max-h-60 overflow-y-auto border rounded-lg divide-y">
                        {foundPoles.map(pole => (
                          <div 
                            key={pole.id}
                            className="p-3 cursor-pointer hover:bg-slate-50 active:bg-slate-100"
                            onClick={() => selectPoleForAdding(pole)}
                          >
                            <div className="flex justify-between items-center">
                              <span className="font-semibold">{pole.length} - {pole.weight}</span>
                              {getStatusBadge(getPoleDisplayStatus(pole))}
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                              SN: {pole.serial_num || 'N/A'} | {pole.brand} | Tag: {pole.tag_id || 'N/A'}
                            </div>
                            {(pole.current_renter || pole.renter_name) && (
                              <div className="text-xs text-blue-600 mt-1 flex items-center gap-1 font-medium">
                                <User className="w-3 h-3" />
                                Rented by: {pole.renter_name || pole.current_renter}
                              </div>
                            )}
                          </div>
                        ))}
                        {/* Create New Pole */}
                        <div 
                          className="p-3 cursor-pointer hover:bg-amber-50 border-t-2 border-dashed border-amber-200"
                          onClick={() => setShowCreatePoleModal(true)}
                        >
                          <div className="flex items-center gap-2 text-amber-700">
                            <Plus className="w-4 h-4" />
                            <span className="font-medium">Don't see it? Create a new pole</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* No results */}
                  {(lengthSearch || weightSearch || searchQuery) && foundPoles.length === 0 && (
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <p className="text-slate-500 mb-3">No poles found</p>
                      <Button variant="outline" onClick={() => setShowCreatePoleModal(true)} className="gap-2">
                        <Plus className="w-4 h-4" />Create New Pole
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                /* Pole Selected - Configure Action */
                <div className="space-y-4">
                  {/* Selected Pole Display */}
                  <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-semibold text-emerald-900">{currentPole.length} - {currentPole.weight}</div>
                        <div className="text-xs text-emerald-700">SN: {currentPole.serial_num || 'N/A'} | {currentPole.brand}</div>
                        {(currentPole.current_renter || currentPole.renter_name) && (
                          <div className="text-xs text-blue-600 mt-1 font-medium flex items-center gap-1">
                            <User className="w-3 h-3" />
                            Rented by: {currentPole.renter_name || currentPole.current_renter}
                          </div>
                        )}
                      </div>
                      {editingIndex === null && (
                        <Button variant="ghost" size="sm" onClick={() => setCurrentPole(null)} className="h-6 w-6 p-0">
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div>
                    <Label className="text-xs mb-2 block">Select Action</Label>
                    <div className="grid grid-cols-3 gap-2">
                      <Button 
                        variant={currentAction === 'out' ? 'default' : 'outline'}
                        className={currentAction === 'out' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                        onClick={() => { setCurrentAction('out'); setCurrentSwapPole(null); }}
                      >
                        <ArrowUpFromLine className="w-4 h-4 mr-1" />Checkout
                      </Button>
                      <Button 
                        variant={currentAction === 'in' ? 'default' : 'outline'}
                        className={currentAction === 'in' ? 'bg-green-600 hover:bg-green-700' : ''}
                        onClick={() => { setCurrentAction('in'); setCurrentSwapPole(null); }}
                      >
                        <ArrowDownToLine className="w-4 h-4 mr-1" />Return
                      </Button>
                      <Button 
                        variant={currentAction === 'swap' ? 'default' : 'outline'}
                        className={currentAction === 'swap' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                        onClick={() => setCurrentAction('swap')}
                      >
                        <Repeat className="w-4 h-4 mr-1" />Swap
                      </Button>
                    </div>
                  </div>

                  {/* Swap Pole Selection */}
                  {currentAction === 'swap' && (
                    <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <Label className="text-xs text-purple-700 mb-2 block">Select swap pole from inventory</Label>
                      
                      {/* Searchable dropdowns for swap */}
                      <div className="grid grid-cols-3 gap-2 mb-2">
                        <div className="relative">
                          <Input 
                            placeholder="Length..." 
                            value={swapLengthSearch}
                            onChange={(e) => setSwapLengthSearch(e.target.value)}
                            onFocus={() => setShowSwapLengthDropdown(true)}
                            onBlur={() => setTimeout(() => setShowSwapLengthDropdown(false), 200)}
                            className="text-sm"
                          />
                          {showSwapLengthDropdown && filteredSwapLengths.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-32 overflow-y-auto">
                              {filteredSwapLengths.map(len => (
                                <div 
                                  key={len} 
                                  className="p-2 text-sm cursor-pointer hover:bg-purple-50"
                                  onMouseDown={() => { setSwapLengthSearch(len); setShowSwapLengthDropdown(false); }}
                                >
                                  {len}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="relative">
                          <Input 
                            placeholder="Weight..." 
                            value={swapWeightSearch}
                            onChange={(e) => setSwapWeightSearch(e.target.value)}
                            onFocus={() => setShowSwapWeightDropdown(true)}
                            onBlur={() => setTimeout(() => setShowSwapWeightDropdown(false), 200)}
                            className="text-sm"
                          />
                          {showSwapWeightDropdown && filteredSwapWeights.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-32 overflow-y-auto">
                              {filteredSwapWeights.map(w => (
                                <div 
                                  key={w} 
                                  className="p-2 text-sm cursor-pointer hover:bg-purple-50"
                                  onMouseDown={() => { setSwapWeightSearch(w.toString()); setShowSwapWeightDropdown(false); }}
                                >
                                  {w}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div>
                          <Input 
                            placeholder="Serial #..." 
                            value={swapSerialSearch}
                            onChange={(e) => setSwapSerialSearch(e.target.value)}
                            className="text-sm"
                          />
                        </div>
                      </div>
                      
                      {/* Swap poles list - showing serial number prominently */}
                      {foundSwapPoles.length > 0 && (
                        <div className="max-h-40 overflow-y-auto border border-purple-200 rounded bg-white divide-y">
                          {foundSwapPoles.map(pole => (
                            <div 
                              key={pole.id}
                              className={`p-2 cursor-pointer text-sm ${currentSwapPole?.id === pole.id ? 'bg-purple-100' : 'hover:bg-purple-50'}`}
                              onClick={() => setCurrentSwapPole(pole)}
                            >
                              <div className="flex justify-between items-center">
                                <span className="font-medium">{pole.length} - {pole.weight}</span>
                                <Badge className="bg-emerald-500 text-white text-xs">Available</Badge>
                              </div>
                              <div className="text-xs text-slate-500">
                                SN: {pole.serial_num || 'N/A'} | {pole.brand} | Tag: {pole.tag_id || 'N/A'}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {(swapLengthSearch || swapWeightSearch || swapSerialSearch) && foundSwapPoles.length === 0 && (
                        <div className="text-sm text-purple-600 p-2">No available poles found</div>
                      )}
                      
                      {currentSwapPole && (
                        <div className="mt-2 p-2 bg-purple-100 rounded flex items-center justify-between">
                          <div className="text-sm text-purple-700">
                            <span className="font-medium">Swap with:</span> SN: {currentSwapPole.serial_num || 'N/A'} | {currentSwapPole.length} - {currentSwapPole.weight} | {currentSwapPole.brand}
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => setCurrentSwapPole(null)} className="h-6 w-6 p-0">
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Add/Update Pole Button */}
                  {editingIndex !== null ? (
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={cancelAdding} className="flex-1">
                        Cancel
                      </Button>
                      <Button onClick={updateSelectedPole} className="flex-1 bg-amber-600 hover:bg-amber-700">
                        <Check className="w-4 h-4 mr-2" />
                        Update Pole
                      </Button>
                    </div>
                  ) : (
                    <Button onClick={confirmAddPole} className="w-full" size="lg">
                      <Plus className="w-4 h-4 mr-2" />
                      Add This Pole
                    </Button>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* Add Pole Button - shows when not adding */
            <Button onClick={startAddingPole} variant="outline" className="w-full mb-4 border-dashed border-2 h-14 text-slate-600 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700">
              <Plus className="w-5 h-5 mr-2" />
              Add Pole
            </Button>
          )}

          {/* User Selection - ALWAYS show when we have poles (regardless of isAddingPole state) */}
          {selectedPoles.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <User className="w-4 h-4 text-amber-600" />
                <h3 className="font-semibold text-slate-900">Select User</h3>
                {suggestedUser && !selectedUser && (
                  <Badge variant="outline" className="text-amber-600 border-amber-400 text-xs ml-2">Suggested: {suggestedUser.full_name}</Badge>
                )}
              </div>

              {selectedUser ? (
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div>
                    <div className="font-medium text-amber-900">{selectedUser.full_name}</div>
                    <div className="text-xs text-amber-600">{selectedUser.email}</div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => { setSelectedUser(null); setUserSearch(''); }}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Input 
                    placeholder="Search by name or email..." 
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="text-base"
                  />
                  {filteredUsers.length > 0 && (
                    <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                      {filteredUsers.map(user => (
                        <div 
                          key={user.id}
                          className={`p-3 cursor-pointer hover:bg-slate-50 ${suggestedUser?.id === user.id ? 'bg-amber-50 border-l-4 border-l-amber-400' : ''}`}
                          onClick={() => { setSelectedUser(user); setUserSearch(user.full_name); }}
                        >
                          <div className="font-medium">{user.full_name}</div>
                          <div className="text-xs text-slate-500">{user.email}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Notes & Submit - ALWAYS visible when we have poles and user selected */}
          {selectedPoles.length > 0 && selectedUser && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4">
              <div className="mb-4">
                <Label className="text-sm mb-2 block">Notes (optional)</Label>
                <Textarea 
                  placeholder="Add any notes..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                />
              </div>
              
              {/* Summary */}
              <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                <div className="text-sm text-slate-600">
                  <span className="font-medium">{selectedUser.full_name}</span> • {selectedPoles.length} pole{selectedPoles.length !== 1 ? 's' : ''}
                  {isAddingPole && currentPole && <span className="text-amber-600 ml-2">(+1 adding...)</span>}
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedPoles.filter(sp => sp.action === 'out').length > 0 && (
                    <Badge className="bg-blue-100 text-blue-700">{selectedPoles.filter(sp => sp.action === 'out').length} Checkout</Badge>
                  )}
                  {selectedPoles.filter(sp => sp.action === 'in').length > 0 && (
                    <Badge className="bg-green-100 text-green-700">{selectedPoles.filter(sp => sp.action === 'in').length} Return</Badge>
                  )}
                  {selectedPoles.filter(sp => sp.action === 'swap').length > 0 && (
                    <Badge className="bg-purple-100 text-purple-700">{selectedPoles.filter(sp => sp.action === 'swap').length} Swap</Badge>
                  )}
                </div>
              </div>
              
              <Button 
                onClick={submitSelectedItems} 
                disabled={submitting}
                className="w-full"
                size="lg"
                data-testid="submit-batch-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
                ) : (
                  <>Submit {selectedPoles.length} Item{selectedPoles.length !== 1 ? 's' : ''}</>
                )}
              </Button>
            </div>
          )}
          
          {/* Create New Pole Modal */}
          <Dialog open={showCreatePoleModal} onOpenChange={setShowCreatePoleModal}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Create New Pole
                </DialogTitle>
                <DialogDescription>
                  Add a new pole to the inventory
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Length *</Label>
                    <Input 
                      placeholder="e.g. 13'6" 
                      value={newPoleData.length}
                      onChange={(e) => setNewPoleData({...newPoleData, length: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Weight *</Label>
                    <Input 
                      placeholder="e.g. 165" 
                      value={newPoleData.weight}
                      onChange={(e) => setNewPoleData({...newPoleData, weight: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm">Brand</Label>
                  <Input 
                    placeholder="e.g. ESSX" 
                    value={newPoleData.brand}
                    onChange={(e) => setNewPoleData({...newPoleData, brand: e.target.value})}
                  />
                </div>
                <div>
                  <Label className="text-sm">Serial Number</Label>
                  <Input 
                    placeholder="Optional" 
                    value={newPoleData.serial_num}
                    onChange={(e) => setNewPoleData({...newPoleData, serial_num: e.target.value})}
                  />
                </div>
                <div>
                  <Label className="text-sm">Tag ID</Label>
                  <Input 
                    placeholder="Optional" 
                    value={newPoleData.tag_id}
                    onChange={(e) => setNewPoleData({...newPoleData, tag_id: e.target.value})}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreatePoleModal(false)}>Cancel</Button>
                <Button onClick={handleCreatePole} disabled={creatingPole}>
                  {creatingPole ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Create Pole
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    );
  }

  // List view
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Header - responsive */}
      <div className="flex items-center justify-between gap-2 mb-4">
        <h2 className="text-sm sm:text-lg font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Checkouts & Returns ({filteredPickupReturns.length})
        </h2>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search name, pole, serial..."
              value={historySearch}
              onChange={(e) => setHistorySearch(e.target.value)}
              className="pl-9 h-8 w-[180px] sm:w-[240px] text-sm"
              data-testid="checkins-search"
            />
            {historySearch && (
              <button onClick={() => setHistorySearch('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                <X className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600" />
              </button>
            )}
          </div>
          <Button onClick={() => setCreateMode(true)} size="sm" className="gap-1 h-8 text-xs sm:text-sm">
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Create New</span>
            <span className="sm:hidden">New</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => fetchPickupReturns(true)} className="h-8 w-8 sm:w-auto p-0 sm:px-3">
            <RefreshCw className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Bulk Status Update Controls */}
      {selectedHistoryIds.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Checkbox 
              checked={selectedHistoryIds.size === allHistoryItemIds.length}
              onCheckedChange={toggleSelectAllHistory}
            />
            <span className="text-sm font-medium text-blue-900">
              {selectedHistoryIds.size} item{selectedHistoryIds.size !== 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-blue-700 mr-2">Change status to:</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={updatingStatus} className="gap-2">
                  {updatingStatus ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Set Status
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => updateProcessingStatus('pending')}>
                  <Badge variant="outline" className="text-amber-600 border-amber-300 mr-2">Pending</Badge>
                  Mark as Pending
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => updateProcessingStatus('in_progress')}>
                  <Badge variant="outline" className="text-blue-600 border-blue-300 mr-2">In Progress</Badge>
                  Mark as In Progress
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => updateProcessingStatus('completed')}>
                  <Badge variant="default" className="bg-emerald-500 mr-2">Completed</Badge>
                  Mark as Completed
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="ghost" size="sm" onClick={() => setSelectedHistoryIds(new Set())}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Recent Activity - Grouped by Batch */}
      {/* Mobile Card View */}
      <div className="sm:hidden space-y-3">
        {paginatedItems.length === 0 ? (
          <div className="text-center text-slate-500 py-8 bg-white rounded-lg border">
            No pickups or checkouts found
          </div>
        ) : (
          paginatedItems.map((item) => 
            item.isGroup ? (
              <div key={`batch-${item.batch_id}`} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <div 
                  className="bg-slate-50 p-3 cursor-pointer flex items-center justify-between"
                  onClick={() => toggleBatch(item.batch_id)}
                >
                  <div className="flex items-center gap-2">
                    {expandedBatches.has(item.batch_id) ? (
                      <ChevronDown className="w-4 h-4 text-slate-500" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-slate-600">IDs: {item.items.map(i => i.id).join(', ')}</span>
                      </div>
                      <div className="text-xs text-slate-500">{formatEST(item.created_at, 'MMM d yyyy, h:mm a')}</div>
                      <div className="font-medium text-sm">{item.items.length} poles • {item.action_type}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.items[0]?.rental_id && (
                      <Badge variant="outline" className="text-xs">#{item.items[0].rental_id}</Badge>
                    )}
                    {getProcessingStatusBadge(item.items[0]?.processing_status)}
                  </div>
                </div>
                {item.items.some(i => i.processing_status !== 'completed') && (
                  <div className="px-3 py-2 border-t border-slate-100 bg-white" onClick={(e) => e.stopPropagation()}>
                    <Button
                      data-testid={`process-all-btn-mobile-${item.batch_id}`}
                      variant="default"
                      size="sm"
                      className="w-full text-xs gap-1.5 bg-indigo-600 hover:bg-indigo-700"
                      onClick={() => processAllInBatch(item)}
                      disabled={processAllRunning}
                    >
                      {processAllRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                      Process All
                    </Button>
                  </div>
                )}
                {expandedBatches.has(item.batch_id) && (
                  <div className="divide-y divide-slate-100">
                    {item.items.map(subItem => (
                      <div key={subItem.id} className="p-3 text-sm">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-xs font-mono text-slate-500 mr-2">#{subItem.id}</span>
                            <span className="font-mono font-medium">{subItem.pole_length} - {subItem.pole_weight}</span>
                            <span className="text-xs text-slate-400 ml-2">#{subItem.pole_serial || subItem.pole_id}</span>
                          </div>
                          {subItem.swap && (
                            <Badge className="bg-purple-100 text-purple-700 text-[10px]">Swap</Badge>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">{subItem.user_name}</div>
                        {subItem.swap && subItem.swap_pole_length && (
                          <div className="flex items-center gap-2 mt-1 text-xs text-slate-600">
                            <span>→ New:</span>
                            <span className="font-mono">{subItem.swap_pole_length} - {subItem.swap_pole_weight}</span>
                          </div>
                        )}
                        {subItem.notes && (
                          <div className="mt-1 p-1.5 bg-slate-50 rounded">
                            <p className="text-xs text-slate-600">
                              <span className="font-medium text-slate-500">Note: </span>
                              {subItem.notes}
                            </p>
                          </div>
                        )}
                        {/* Action buttons for each item in group */}
                        <div className="flex items-center gap-2 mt-2">
                          {subItem.processing_status !== 'completed' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openProcessDialog(subItem)}
                              className="flex-1 text-xs"
                              data-testid={`process-btn-mobile-${subItem.id}`}
                            >
                              <Play className="w-3 h-3 mr-1" />
                              Process {subItem.swap ? 'Swap' : (subItem.status === 0 ? 'Checkout' : 'Return')}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(subItem)}
                            className="text-slate-500 hover:text-blue-600 hover:bg-blue-50 h-8 w-8 p-0"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deletePickupReturn(subItem.id, subItem.swap ? 'swap' : (subItem.status === 0 ? 'checkout' : 'return'))}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div key={item.id} className="bg-white border border-slate-200 rounded-lg p-3">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono font-medium text-slate-600">#{item.id}</span>
                      <span className="text-xs text-slate-500">{formatEST(item.created_at, 'MMM d yyyy, h:mm a')}</span>
                    </div>
                    {item.swap ? (
                      <div className="space-y-1 mt-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px] bg-green-50 text-green-600 border-green-200 px-1">↩ Return</Badge>
                          <span className="font-mono text-sm">{item.pole_length} - {item.pole_weight}</span>
                          <span className="text-xs text-slate-400">#{item.pole_code}</span>
                        </div>
                        {item.swap_pole_length && (
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-600 border-blue-200 px-1">↗ Checkout</Badge>
                            <span className="font-mono text-sm">{item.swap_pole_length} - {item.swap_pole_weight}</span>
                            <span className="text-xs text-slate-400">#{item.swap_pole_code}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="font-mono font-medium text-sm mt-0.5">
                        {item.pole_length} - {item.pole_weight}
                        <span className="text-slate-400 font-normal ml-2">#{item.pole_serial || item.pole_id}</span>
                      </div>
                    )}
                  </div>
                  {item.swap ? (
                    <Badge className="bg-purple-100 text-purple-700">Swap</Badge>
                  ) : (
                    <Badge className={item.status === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}>
                      {item.status === 1 ? 'Return' : 'Checkout'}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-600">{item.user_name || 'Unknown'}</span>
                  <div className="flex items-center gap-2">
                    {item.rental_id && <span className="text-slate-400">Rental #{item.rental_id}</span>}
                    {getProcessingStatusBadge(item.processing_status)}
                  </div>
                </div>
                {item.notes && (
                  <div className="mt-2 p-2 bg-slate-50 rounded-md">
                    <p className="text-xs text-slate-600">
                      <span className="font-medium text-slate-500">Note: </span>
                      {item.notes}
                    </p>
                  </div>
                )}
                {/* Action buttons for mobile */}
                <div className="flex items-center gap-2 mt-3">
                  {item.processing_status !== 'completed' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openProcessDialog(item)}
                      className="flex-1 text-xs"
                    >
                      <Play className="w-3 h-3 mr-1" />
                      Process {item.swap ? 'Swap' : (item.status === 0 ? 'Checkout' : 'Return')}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditDialog(item)}
                    className="text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deletePickupReturn(item.id, item.swap ? 'swap' : (item.status === 0 ? 'checkout' : 'return'))}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )
          )
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden sm:block bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">
                  <Checkbox 
                    checked={allHistoryItemIds.length > 0 && selectedHistoryIds.size === allHistoryItemIds.length}
                    onCheckedChange={toggleSelectAllHistory}
                    data-testid="select-all-history"
                  />
                </TableHead>
                <TableHead className="w-[60px]">ID</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Pole Details</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[100px]">Process</TableHead>
              </TableRow>
            </TableHeader>
          <TableBody>
            {paginatedItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-slate-500 py-8">
                  No pickups or checkouts found
                </TableCell>
              </TableRow>
            ) : (
              paginatedItems.map((item, idx) => 
                item.isGroup ? (
                  /* Batch Group Header */
                  <Fragment key={`batch-${item.batch_id}`}>
                    <TableRow 
                      className="bg-slate-50 cursor-pointer hover:bg-slate-100"
                    >
                      <TableCell className="w-[40px]" onClick={(e) => e.stopPropagation()}>
                        <Checkbox 
                          checked={item.items.every(i => selectedHistoryIds.has(i.id))}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedHistoryIds(prev => {
                                const newSet = new Set(prev);
                                item.items.forEach(i => newSet.add(i.id));
                                return newSet;
                              });
                            } else {
                              setSelectedHistoryIds(prev => {
                                const newSet = new Set(prev);
                                item.items.forEach(i => newSet.delete(i.id));
                                return newSet;
                              });
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-xs text-slate-500" onClick={() => toggleBatch(item.batch_id)}>
                        {item.items.map(i => i.id).join(', ')}
                      </TableCell>
                      <TableCell className="font-mono text-sm" onClick={() => toggleBatch(item.batch_id)}>
                        <div className="flex items-center gap-2">
                          {expandedBatches.has(item.batch_id) ? (
                            <ChevronDown className="w-4 h-4 text-slate-500" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-500" />
                          )}
                          {formatEST(item.created_at, 'MMM d yyyy, h:mm a')}
                        </div>
                      </TableCell>
                      <TableCell onClick={() => toggleBatch(item.batch_id)}>
                        <div>
                          <div className="font-medium text-slate-900">{item.items[0]?.user_name || 'Unknown'}</div>
                          {item.items[0]?.rental_id && (
                            <Link to={`/rentals/${item.items[0].rental_id}`} className="text-blue-600 hover:underline text-xs" onClick={(e) => e.stopPropagation()}>
                              Rental #{item.items[0].rental_id}
                            </Link>
                          )}
                        </div>
                      </TableCell>
                      <TableCell onClick={() => toggleBatch(item.batch_id)}>
                        <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                          {item.items.length} poles
                        </Badge>
                      </TableCell>
                      <TableCell onClick={() => toggleBatch(item.batch_id)}>
                        {/* Action column blank for batch parent - individual items show their own actions */}
                        <span className="text-xs text-slate-400">—</span>
                      </TableCell>
                      <TableCell onClick={() => toggleBatch(item.batch_id)} className="max-w-[150px]">
                        {item.items[0]?.notes ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-xs text-slate-600 truncate block cursor-help">
                                {item.items[0].notes}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[300px] whitespace-pre-wrap bg-slate-900 text-white">
                              {item.items[0].notes}
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <span className="text-xs text-slate-300">-</span>
                        )}
                      </TableCell>
                      <TableCell onClick={() => toggleBatch(item.batch_id)}>
                        {getProcessingStatusBadge(item.items[0]?.processing_status)}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {item.items.some(i => i.processing_status !== 'completed') ? (
                          <Button
                            data-testid={`process-all-btn-${item.batch_id}`}
                            variant="default"
                            size="sm"
                            className="text-xs h-8 gap-1.5 bg-indigo-600 hover:bg-indigo-700"
                            onClick={() => processAllInBatch(item)}
                            disabled={processAllRunning}
                          >
                            {processAllRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                            Process All
                          </Button>
                        ) : (
                          <span className="text-xs text-slate-400">Done</span>
                        )}
                      </TableCell>
                    </TableRow>
                    {/* Expanded items */}
                    {expandedBatches.has(item.batch_id) && item.items.map((subItem, subIdx) => (
                      <TableRow key={subItem.id} className="bg-slate-25 border-l-4 border-l-indigo-200">
                        <TableCell className="w-[40px]">
                          <Checkbox 
                            checked={selectedHistoryIds.has(subItem.id)}
                            onCheckedChange={() => toggleHistorySelection(subItem.id)}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-xs text-slate-600 font-medium">
                          #{subItem.id}
                        </TableCell>
                        <TableCell className="pl-6 text-xs text-slate-400">
                          └ {subIdx + 1}/{item.items.length}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{subItem.user_name || '-'}</TableCell>
                        <TableCell>
                          {subItem.swap ? (
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">↩ Return</Badge>
                                <span className="font-mono text-sm">{subItem.pole_length} - {subItem.pole_weight}</span>
                                <span className="text-xs text-slate-400">#{subItem.pole_code}</span>
                              </div>
                              {subItem.swap_pole_length ? (
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">↗ Checkout</Badge>
                                  <span className="font-mono text-sm">{subItem.swap_pole_length} - {subItem.swap_pole_weight}</span>
                                  <span className="text-xs text-slate-400">#{subItem.swap_pole_code}</span>
                                </div>
                              ) : (
                                <div className="text-xs text-slate-400 italic">Swap pole not specified</div>
                              )}
                            </div>
                          ) : (
                            <div className="text-sm">
                              <span className="font-mono font-medium">{subItem.pole_length} - {subItem.pole_weight}</span>
                              <div className="text-xs text-slate-400">#{subItem.pole_code} • SN: {subItem.pole_serial || '-'}</div>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>{getActionBadge(subItem.status, subItem.swap)}</TableCell>
                        <TableCell className="max-w-[150px]">
                          {subItem.notes ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-xs text-slate-600 truncate block cursor-help">
                                  {subItem.notes}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-[300px] whitespace-pre-wrap bg-slate-900 text-white">
                                {subItem.notes}
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <span className="text-xs text-slate-300">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {getProcessingStatusBadge(subItem.processing_status)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(subItem)}
                              className="text-slate-500 hover:text-blue-600 hover:bg-blue-50 h-8 w-8 p-0"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {subItem.processing_status !== 'completed' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openProcessDialog(subItem)}
                                className="text-xs"
                                data-testid={`process-btn-${subItem.id}`}
                              >
                                <Play className="w-3 h-3 mr-1" />
                                Process
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deletePickupReturn(subItem.id, subItem.swap ? 'swap' : (subItem.status === 0 ? 'checkout' : 'return'))}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </Fragment>
                ) : (
                  /* Single item (no batch) */
                  <TableRow key={item.id}>
                    <TableCell className="w-[40px]">
                      <Checkbox 
                        checked={selectedHistoryIds.has(item.id)}
                        onCheckedChange={() => toggleHistorySelection(item.id)}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs text-slate-600 font-medium">
                      #{item.id}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {formatEST(item.created_at, 'MMM d yyyy, h:mm a')}
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium text-slate-900">{item.user_name || 'Unknown'}</div>
                        {item.rental_id && (
                          <Link to={`/rentals/${item.rental_id}`} className="text-blue-600 hover:underline text-xs">
                            Rental #{item.rental_id}
                          </Link>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {item.swap ? (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">↩ Return</Badge>
                            <span className="font-mono text-sm">{item.pole_length} - {item.pole_weight}</span>
                            <span className="text-xs text-slate-400">#{item.pole_code}</span>
                          </div>
                          {item.swap_pole_length ? (
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">↗ Checkout</Badge>
                              <span className="font-mono text-sm">{item.swap_pole_length} - {item.swap_pole_weight}</span>
                              <span className="text-xs text-slate-400">#{item.swap_pole_code}</span>
                            </div>
                          ) : (
                            <div className="text-xs text-slate-400 italic">Swap pole not specified</div>
                          )}
                        </div>
                      ) : (
                        <div className="text-sm">
                          <span className="font-mono font-medium">{item.pole_length} - {item.pole_weight}</span>
                          <div className="text-xs text-slate-400">#{item.pole_code} • SN: {item.pole_serial || '-'}</div>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{getActionBadge(item.status, item.swap)}</TableCell>
                    <TableCell className="max-w-[150px]">
                      {item.notes ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-xs text-slate-600 truncate block cursor-help">
                              {item.notes}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[300px] whitespace-pre-wrap bg-slate-900 text-white">
                            {item.notes}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <span className="text-xs text-slate-300">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {getProcessingStatusBadge(item.processing_status)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(item)}
                          className="text-slate-500 hover:text-blue-600 hover:bg-blue-50 h-8 w-8 p-0"
                          data-testid={`edit-btn-${item.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {item.processing_status !== 'completed' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openProcessDialog(item)}
                            className="text-xs"
                            data-testid={`process-btn-${item.id}`}
                          >
                            <Play className="w-3 h-3 mr-1" />
                            Process
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deletePickupReturn(item.id, item.swap ? 'swap' : (item.status === 0 ? 'checkout' : 'return'))}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                          data-testid={`delete-btn-${item.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              )
            )}
          </TableBody>
        </Table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 px-1">
          <div className="text-sm text-slate-500">
            Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, groupedPickupReturns.length)} of {groupedPickupReturns.length}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="h-8"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm text-slate-600 font-medium px-2">
              {currentPage} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="h-8"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Edit Check-in/Checkout Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Edit Record #{editingItem?.id}
            </DialogTitle>
            <DialogDescription>
              {editingItem?.pole_length} - {editingItem?.pole_weight} {editingItem?.pole_code ? `(${editingItem.pole_code})` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* User Selection */}
            <div>
              <Label>User</Label>
              <div className="mt-1">
                {(() => {
                  const selectedUser = users.find(u => u.id === editForm.user_id);
                  return selectedUser ? (
                    <div className="flex items-center justify-between p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
                      <div>
                        <div className="font-medium text-sm">{selectedUser.full_name}</div>
                        <div className="text-xs text-slate-500">{selectedUser.email}</div>
                      </div>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditForm({ ...editForm, user_id: null })}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          placeholder="Search users..."
                          value={editUserSearch}
                          onChange={(e) => setEditUserSearch(e.target.value)}
                          className="pl-9 h-9"
                        />
                      </div>
                      {editUserSearch.trim() && (
                        <div className="max-h-[150px] overflow-y-auto border border-slate-200 rounded-lg">
                          {users
                            .filter(u => {
                              const q = editUserSearch.toLowerCase();
                              return u.full_name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
                            })
                            .slice(0, 10)
                            .map(u => (
                              <div
                                key={u.id}
                                onClick={() => { setEditForm({ ...editForm, user_id: u.id }); setEditUserSearch(''); }}
                                className="p-2 hover:bg-slate-100 cursor-pointer border-b border-slate-100 last:border-b-0"
                              >
                                <div className="font-medium text-sm">{u.full_name}</div>
                                <div className="text-xs text-slate-500">{u.email}</div>
                              </div>
                            ))
                          }
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Action Type */}
            <div>
              <Label>Action Type</Label>
              <div className="flex gap-2 mt-1">
                <Button
                  variant={editForm.status === 0 ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setEditForm({ ...editForm, status: 0 })}
                >
                  <ArrowUpFromLine className="w-4 h-4 mr-1" />
                  Checkout
                </Button>
                <Button
                  variant={editForm.status === 1 ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setEditForm({ ...editForm, status: 1 })}
                >
                  <ArrowDownToLine className="w-4 h-4 mr-1" />
                  Return
                </Button>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label>Notes</Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                placeholder="Add notes..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={savingEdit}>
              {savingEdit ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Process Action Dialog */}
      <Dialog open={processDialogOpen} onOpenChange={setProcessDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="w-5 h-5 text-emerald-600" />
              Process {processingItem?.swap ? 'Swap' : (processingItem?.status === 0 ? 'Checkout' : 'Return')}
            </DialogTitle>
            <DialogDescription>
              {processingItem?.swap ? (
                <>Swap {processingItem?.pole_length} for {processingItem?.swap_pole_length}</>
              ) : processingItem?.status === 0 ? (
                <>Add pole {processingItem?.pole_length}-{processingItem?.pole_weight} to a rental for {processingItem?.user_name}</>
              ) : (
                <>Return pole {processingItem?.pole_length}-{processingItem?.pole_weight} from rental</>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {/* Checkout: Select or create rental */}
            {processingItem?.status === 0 && !processingItem?.swap && (
              <div className="space-y-3">
                {loadingRentals ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                  </div>
                ) : eligibleRentals.length > 0 ? (
                  <>
                    <Label>Select an existing rental or create a new one:</Label>
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {eligibleRentals.map(rental => (
                        <div
                          key={rental.id}
                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                            selectedRentalId === rental.id 
                              ? 'border-emerald-500 bg-emerald-50' 
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                          onClick={() => handleRentalSelect(rental.id)}
                        >
                          <div className="flex justify-between items-center">
                            <span className="font-medium">Rental #{rental.id}</span>
                            <div className="flex items-center gap-2">
                              {rental.subscription_status === 'active' && (
                                <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                  <CreditCard className="w-3 h-3 mr-1" />Active Sub
                                </Badge>
                              )}
                              <Badge variant="outline" className={rental.status_name === 'Active' ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : ''}>
                                {rental.status_name}
                              </Badge>
                            </div>
                          </div>
                          <div className="text-xs text-slate-500 mt-1">
                            {rental.pole_count} pole(s) • Created {new Date(rental.created_at).toLocaleDateString()}
                          </div>
                          {rental.status_name === 'Active' && rental.subscription_status === 'active' && (
                            <div className="text-xs text-emerald-600 mt-1">
                              ✓ Pole will be added to existing subscription
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <span>or</span>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setSelectedRentalId('new')}
                        className={selectedRentalId === 'new' ? 'border-emerald-500 bg-emerald-50' : ''}
                      >
                        <Plus className="w-3 h-3 mr-1" />Create New Rental
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-sm text-amber-800">
                      No eligible rentals found for {processingItem?.user_name}. A new rental will be created.
                    </p>
                  </div>
                )}
                
                {/* Proration Billing Checkbox - show when rental with active subscription is selected */}
                {selectedRentalId && selectedRentalId !== 'new' && eligibleRentals.find(r => r.id === selectedRentalId)?.subscription_status === 'active' && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <Checkbox 
                        id="chargeProrata"
                        checked={chargeProrata}
                        onCheckedChange={setChargeProrata}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <label htmlFor="chargeProrata" className="text-sm font-medium text-blue-800 cursor-pointer">
                          Charge prorated amount now
                        </label>
                        {prorataInfo ? (
                          <div className="text-xs text-blue-600 mt-1 space-y-1">
                            <div className="font-mono bg-white/50 rounded p-2 mt-2">
                              <div className="flex justify-between">
                                <span>Monthly rate:</span>
                                <span>${prorataInfo.monthly_price?.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Daily rate:</span>
                                <span>${prorataInfo.daily_rate?.toFixed(2)} (÷30 days)</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Days until renewal:</span>
                                <span>{prorataInfo.days_remaining} days</span>
                              </div>
                              <div className="flex justify-between font-bold border-t pt-1 mt-1">
                                <span>Prorated charge:</span>
                                <span>${prorataInfo.amount?.toFixed(2)}</span>
                              </div>
                            </div>
                            <p className="text-blue-500 italic">
                              {chargeProrata 
                                ? "This amount will be invoiced immediately" 
                                : "No proration will be charged - pole added at renewal"}
                            </p>
                          </div>
                        ) : (
                          <p className="text-xs text-blue-500 mt-1">Loading proration info...</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Return: Show rental info */}
            {processingItem?.status === 1 && !processingItem?.swap && (
              <div className="space-y-3">
                {processingItem?.rental_id ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      This pole will be removed from <strong>Rental #{processingItem.rental_id}</strong>.
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      The subscription will be updated to reflect the new pole count. If this is the last pole, the rental will be marked as completed.
                    </p>
                  </div>
                ) : (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-800">
                      Warning: This pole is not linked to any active rental.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Swap: Show swap details */}
            {processingItem?.swap && (
              <div className="space-y-3">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-green-100 text-green-700">Return</Badge>
                    <span className="font-mono text-sm">{processingItem?.pole_length} - {processingItem?.pole_weight}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-100 text-blue-700">Checkout</Badge>
                    <span className="font-mono text-sm">{processingItem?.swap_pole_length} - {processingItem?.swap_pole_weight}</span>
                  </div>
                  {processingItem?.rental_id && (
                    <p className="text-xs text-purple-600 mt-2">
                      Updates Rental #{processingItem.rental_id}
                    </p>
                  )}
                </div>
                
                {/* Charge Swap Fee Checkbox */}
                <div className="flex items-center space-x-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <Checkbox 
                    id="chargeSwapFee"
                    checked={chargeSwapFee}
                    onCheckedChange={setChargeSwapFee}
                  />
                  <div className="flex-1">
                    <Label htmlFor="chargeSwapFee" className="text-sm font-medium cursor-pointer">
                      Charge swap fee now - <span className="text-amber-700 font-bold">$35.00</span>
                    </Label>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {chargeSwapFee 
                        ? "✓ $35.00 will be charged immediately to customer's card"
                        : "No swap fee will be charged"
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setProcessDialogOpen(false)} disabled={isProcessing}>
              Cancel
            </Button>
            <Button 
              onClick={handleProcessAction} 
              disabled={isProcessing || (processingItem?.status === 0 && !processingItem?.swap && eligibleRentals.length > 0 && !selectedRentalId)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {isProcessing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
              ) : (
                <><Check className="w-4 h-4 mr-2" />Confirm</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== RENTALS TAB ====================

export default CheckInsOutsTab;
